<script lang="ts">
  import { onMount } from 'svelte';
  import type { ConnectionSummary, MqttBrokerSummary, MqttChannelSummary, MqttQos } from '@ucc/shared';
  import {
    ApiError,
    createConnection,
    createMqttChannel,
    createOrUpdateMqttBroker,
    deleteConnection,
    deleteMqttBroker,
    deleteMqttChannel,
    fetchConnections,
    fetchMqttBroker,
    fetchMqttChannels,
    updateConnection,
    updateMqttChannel,
  } from '$lib/api';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  const CAP = 5;
  const DEFAULT_MQTT_PORT = 8883; // MQTTS (MQTT over TLS) industry-standard port.
  const PLACEHOLDER_HINT =
    'Tokens available: {uid}, {deviceName}, {groupName}, {timestamp}, {recordId} — any combination.';

  const COMMON_HEADERS = [
    'Authorization',
    'X-API-Key',
    'Content-Type',
    'Accept',
    'Cache-Control',
    'Content-Length',
    'Content-MD5',
    'Content-Transfer-Encoding',
    'Cookie',
    'User-Agent',
    'X-Requested-With',
    'Access-Control-Request-Method',
  ];

  function suggestClientId(): string {
    const id = typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}`;
    return `ucc-${id.replace(/-/g, '').slice(0, 8)}`;
  }

  type HeaderRow = { name: string; value: string };

  let topTab = $state<'https' | 'mqtt'>('https');
  let loading = $state(true);
  let error = $state<string | null>(null);

  // --- HTTPS Endpoints ---
  let httpsConnections = $state<ConnectionSummary[]>([]);
  let editingConnId = $state<string | null>(null);
  let connName = $state('');
  let connUrl = $state('');
  let headerRows = $state<HeaderRow[]>([]);
  let savingConn = $state(false);
  let connFormError = $state<string | null>(null);
  let connFormOk = $state<string | null>(null);
  let busyDeleteConnId = $state<string | null>(null);
  let connDeleteError = $state<string | null>(null);

  // --- MQTT Broker ---
  let broker = $state<MqttBrokerSummary | null>(null);
  let brokerHost = $state('');
  let brokerPort = $state<number | ''>(DEFAULT_MQTT_PORT);
  let brokerClientId = $state('');
  let brokerQos = $state<MqttQos>(1);
  let brokerUsername = $state('');
  let brokerPassword = $state('');
  let brokerCaCert = $state('');
  let brokerClientCert = $state('');
  let brokerClientKey = $state('');
  let savingBroker = $state(false);
  let brokerFormError = $state<string | null>(null);
  let brokerFormOk = $state<string | null>(null);
  let deletingBroker = $state(false);

  // --- MQTT Channels ---
  let channels = $state<MqttChannelSummary[]>([]);
  let editingChannelId = $state<string | null>(null);
  let channelName = $state('');
  let channelPubTopic = $state('');
  let channelSubTopic = $state('');
  let savingChannel = $state(false);
  let channelFormError = $state<string | null>(null);
  let channelFormOk = $state<string | null>(null);
  let busyDeleteChannelId = $state<string | null>(null);
  let channelDeleteError = $state<string | null>(null);

  const atConnCap = $derived(httpsConnections.length >= CAP);
  const atChannelCap = $derived(channels.length >= CAP);

  function resetConnForm() {
    editingConnId = null;
    connName = '';
    connUrl = '';
    headerRows = [];
    connFormError = null;
    connFormOk = null;
  }

  function resetBrokerForm() {
    brokerHost = broker?.host ?? '';
    brokerPort = broker?.port ?? DEFAULT_MQTT_PORT;
    brokerClientId = broker?.clientId ?? suggestClientId();
    brokerQos = broker?.qos ?? 1;
    brokerUsername = broker?.username ?? '';
    brokerPassword = '';
    brokerCaCert = '';
    brokerClientCert = '';
    brokerClientKey = '';
    brokerFormError = null;
    brokerFormOk = null;
  }

  function resetChannelForm() {
    editingChannelId = null;
    channelName = '';
    channelPubTopic = '';
    channelSubTopic = '';
    channelFormError = null;
    channelFormOk = null;
  }

  async function load() {
    loading = true;
    error = null;
    try {
      const [connRes, brokerRes, channelRes] = await Promise.all([
        fetchConnections(),
        fetchMqttBroker(),
        fetchMqttChannels(),
      ]);
      httpsConnections = connRes.connections;
      broker = brokerRes.broker;
      channels = channelRes.channels;
      resetBrokerForm();
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load connections';
    } finally {
      loading = false;
    }
  }

  function addHeaderRow() {
    headerRows = [...headerRows, { name: '', value: '' }];
  }

  function removeHeaderRow(i: number) {
    headerRows = headerRows.filter((_, idx) => idx !== i);
  }

  function editConnection(c: ConnectionSummary) {
    resetConnForm();
    editingConnId = c.id;
    connName = c.name;
    connUrl = c.url;
    headerRows = Object.entries(c.headers).map(([k, v]) => ({ name: k, value: v }));
  }

  function headersFromRows(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const row of headerRows) {
      const k = row.name.trim();
      if (k) out[k] = row.value;
    }
    return out;
  }

  async function onSaveConnection(e: Event) {
    e.preventDefault();
    connFormError = null;
    connFormOk = null;
    const name = connName.trim();
    const url = connUrl.trim();
    if (!name || !url) {
      connFormError = 'Name and URL are required.';
      return;
    }
    savingConn = true;
    try {
      if (editingConnId) {
        const res = await updateConnection(editingConnId, { name, url, headers: headersFromRows() });
        httpsConnections = httpsConnections.map((c) => (c.id === editingConnId ? res.connection : c));
      } else {
        const res = await createConnection({ name, url, headers: headersFromRows() });
        httpsConnections = [...httpsConnections, res.connection];
      }
      connFormOk = editingConnId ? 'Connection updated.' : 'Connection created.';
      resetConnForm();
    } catch (err) {
      connFormError = err instanceof ApiError ? err.message : 'Save failed';
    } finally {
      savingConn = false;
    }
  }

  async function onDeleteConnection(c: ConnectionSummary) {
    if (!window.confirm(`Delete connection "${c.name}"? Any group using it will stop delivering upstream.`)) {
      return;
    }
    connDeleteError = null;
    busyDeleteConnId = c.id;
    try {
      await deleteConnection(c.id);
      httpsConnections = httpsConnections.filter((x) => x.id !== c.id);
      if (editingConnId === c.id) resetConnForm();
    } catch (err) {
      connDeleteError = err instanceof ApiError ? err.message : 'Delete failed';
    } finally {
      busyDeleteConnId = null;
    }
  }

  async function onSaveBroker(e: Event) {
    e.preventDefault();
    brokerFormError = null;
    brokerFormOk = null;
    const host = brokerHost.trim();
    const clientId = brokerClientId.trim();
    const port = Number(brokerPort);
    if (!host || !clientId || !port) {
      brokerFormError = 'Host, port and client id are required.';
      return;
    }
    if (port < 1 || port > 65535) {
      brokerFormError = 'Port must be between 1 and 65535.';
      return;
    }
    if (brokerPassword.trim() && !brokerUsername.trim()) {
      brokerFormError = 'Username is required when a password is set (MQTT protocol requirement).';
      return;
    }
    const existingHasCert = Boolean(broker?.hasClientCert);
    const existingHasKey = Boolean(broker?.hasClientKey);
    const effectiveHasCert = brokerClientCert.trim() ? true : existingHasCert;
    const effectiveHasKey = brokerClientKey.trim() ? true : existingHasKey;
    if (effectiveHasCert !== effectiveHasKey) {
      brokerFormError = 'Client certificate and client private key must be provided together.';
      return;
    }
    savingBroker = true;
    try {
      const res = await createOrUpdateMqttBroker({
        host,
        port,
        clientId,
        qos: brokerQos,
        username: brokerUsername.trim() || undefined,
        password: brokerPassword.trim() || undefined,
        caCert: brokerCaCert.trim() || undefined,
        clientCert: brokerClientCert.trim() || undefined,
        clientKey: brokerClientKey.trim() || undefined,
      });
      broker = res.broker;
      resetBrokerForm();
      brokerFormOk = 'Saved.';
    } catch (err) {
      brokerFormError = err instanceof ApiError ? err.message : 'Save failed';
    } finally {
      savingBroker = false;
    }
  }

  async function onDeleteBroker() {
    if (
      !window.confirm(
        'Delete your MQTT broker? Every channel built on it will be removed too, and any group using one will stop delivering upstream.',
      )
    ) {
      return;
    }
    brokerFormError = null;
    deletingBroker = true;
    try {
      await deleteMqttBroker();
      broker = null;
      channels = [];
      resetBrokerForm();
    } catch (err) {
      brokerFormError = err instanceof ApiError ? err.message : 'Delete failed';
    } finally {
      deletingBroker = false;
    }
  }

  function editChannel(c: MqttChannelSummary) {
    resetChannelForm();
    editingChannelId = c.id;
    channelName = c.name;
    channelPubTopic = c.pubTopic;
    channelSubTopic = c.subTopic ?? '';
  }

  async function onSaveChannel(e: Event) {
    e.preventDefault();
    channelFormError = null;
    channelFormOk = null;
    const name = channelName.trim();
    const pubTopic = channelPubTopic.trim();
    if (!name || !pubTopic) {
      channelFormError = 'Name and publish topic are required.';
      return;
    }
    savingChannel = true;
    try {
      if (editingChannelId) {
        const res = await updateMqttChannel(editingChannelId, {
          name,
          pubTopic,
          subTopic: channelSubTopic.trim() || null,
        });
        channels = channels.map((c) => (c.id === editingChannelId ? res.channel : c));
      } else {
        const res = await createMqttChannel({ name, pubTopic, subTopic: channelSubTopic.trim() || undefined });
        channels = [...channels, res.channel];
      }
      channelFormOk = editingChannelId ? 'Channel updated.' : 'Channel created.';
      resetChannelForm();
    } catch (err) {
      channelFormError = err instanceof ApiError ? err.message : 'Save failed';
    } finally {
      savingChannel = false;
    }
  }

  async function onDeleteChannel(c: MqttChannelSummary) {
    if (!window.confirm(`Delete channel "${c.name}"? Any group using it will stop delivering upstream.`)) {
      return;
    }
    channelDeleteError = null;
    busyDeleteChannelId = c.id;
    try {
      await deleteMqttChannel(c.id);
      channels = channels.filter((x) => x.id !== c.id);
      if (editingChannelId === c.id) resetChannelForm();
    } catch (err) {
      channelDeleteError = err instanceof ApiError ? err.message : 'Delete failed';
    } finally {
      busyDeleteChannelId = null;
    }
  }

  onMount(() => {
    void load();
  });
</script>

<datalist id="common-header-names">
  {#each COMMON_HEADERS as h (h)}
    <option value={h}></option>
  {/each}
</datalist>

<div class="page">
  <a class="back" href="#/settings">← Settings</a>
  <h2>Connections</h2>
  <p class="sub">
    Where UCC delivers your groups' upstream data. UCC always connects out as an MQTT client — it
    never hosts a broker, and your organization has exactly one broker connection shared by up to
    {CAP} channels.
  </p>

  <div class="tabs">
    <button class="tab" class:active={topTab === 'https'} onclick={() => (topTab = 'https')}>
      HTTPS Endpoints
    </button>
    <button class="tab" class:active={topTab === 'mqtt'} onclick={() => (topTab = 'mqtt')}>
      MQTT
    </button>
  </div>

  {#if error}
    <Card><p class="err" role="alert">{error}</p></Card>
  {:else if loading}
    <Card><p class="muted">Loading…</p></Card>
  {:else if topTab === 'https'}
    <div class="grid">
      <Card title="HTTPS endpoints" subtitle={`${httpsConnections.length} of ${CAP} used`} padded={false}>
        {#if httpsConnections.length === 0}
          <p class="banner muted">No connections yet.</p>
        {:else}
          <ul class="list">
            {#each httpsConnections as c (c.id)}
              <li class:selected={editingConnId === c.id}>
                <button class="row" onclick={() => editConnection(c)}>
                  <span class="name">{c.name}</span>
                  <span class="meta">{c.url}</span>
                </button>
                <Button variant="ghost" size="sm" disabled={busyDeleteConnId === c.id} onclick={() => void onDeleteConnection(c)}>
                  {busyDeleteConnId === c.id ? '…' : 'Delete'}
                </Button>
              </li>
            {/each}
          </ul>
        {/if}
        {#if connDeleteError}
          <p class="err banner-pad" role="alert">{connDeleteError}</p>
        {/if}
      </Card>

      <Card title={editingConnId ? 'Edit connection' : 'New connection'} subtitle={atConnCap && !editingConnId ? `Limit of ${CAP} reached` : undefined}>
        {#if atConnCap && !editingConnId}
          <p class="muted">Delete an existing connection before adding another.</p>
        {:else}
          <form class="form" onsubmit={(e) => void onSaveConnection(e)}>
            <label class="field">
              <span>Name</span>
              <input type="text" bind:value={connName} maxlength="120" disabled={savingConn} required />
            </label>
            <label class="field">
              <span>URL</span>
              <input
                type="text"
                bind:value={connUrl}
                placeholder="https://platform.example.com/ingest/{'{uid}'}"
                disabled={savingConn}
                required
              />
              <span class="field-hint">{PLACEHOLDER_HINT}</span>
            </label>
            <div class="field">
              <span>Headers</span>
              {#if headerRows.length}
                <div class="header-rows">
                  {#each headerRows as row, i}
                    <div class="header-row">
                      <input type="text" list="common-header-names" placeholder="Header name" bind:value={row.name} disabled={savingConn} />
                      <input type="text" placeholder="Value" bind:value={row.value} disabled={savingConn} />
                      <button type="button" class="remove-row" disabled={savingConn} onclick={() => removeHeaderRow(i)} aria-label="Remove header">×</button>
                    </div>
                  {/each}
                </div>
              {/if}
              <Button type="button" variant="secondary" size="sm" disabled={savingConn} onclick={addHeaderRow}>+ Add header</Button>
              <span class="field-hint">Values may use the same tokens as the URL — {PLACEHOLDER_HINT}</span>
            </div>
            <div class="form-actions">
              <Button type="submit" disabled={savingConn}>
                {savingConn ? 'Saving…' : editingConnId ? 'Save changes' : 'Create'}
              </Button>
              {#if editingConnId}
                <Button type="button" variant="ghost" disabled={savingConn} onclick={resetConnForm}>Cancel</Button>
              {/if}
            </div>
          </form>
        {/if}
        {#if connFormError}
          <p class="err" role="alert">{connFormError}</p>
        {/if}
        {#if connFormOk}
          <p class="ok" role="status">{connFormOk}</p>
        {/if}
      </Card>
    </div>
  {:else}
    <div class="stack">
      <Card title="MQTT broker" subtitle="Your organization's one broker connection">
        <form class="form" onsubmit={(e) => void onSaveBroker(e)}>
          <div class="row2">
            <label class="field">
              <span>Host</span>
              <input type="text" bind:value={brokerHost} disabled={savingBroker} required />
            </label>
            <label class="field">
              <span>Port</span>
              <input type="number" bind:value={brokerPort} min="1" max="65535" disabled={savingBroker} required />
            </label>
          </div>
          <div class="row2">
            <label class="field">
              <span>Client id</span>
              <input type="text" bind:value={brokerClientId} disabled={savingBroker} required />
            </label>
            <label class="field">
              <span>QoS</span>
              <select bind:value={brokerQos} disabled={savingBroker}>
                <option value={0}>• 0 — At most once</option>
                <option value={1}>• 1 — At least once</option>
                <option value={2}>• 2 — Exactly once</option>
              </select>
            </label>
          </div>
          <p class="field-hint">QoS applies to every channel on this broker.</p>
          <label class="field">
            <span>Username (optional)</span>
            <input type="text" bind:value={brokerUsername} disabled={savingBroker} autocomplete="off" />
          </label>
          <label class="field">
            <span>Password {broker?.hasPassword ? '(set — leave blank to keep)' : '(optional)'}</span>
            <input type="password" bind:value={brokerPassword} disabled={savingBroker} autocomplete="new-password" />
          </label>
          <label class="field">
            <span>CA certificate {broker?.hasCaCert ? '(set — leave blank to keep)' : '(optional, PEM)'}</span>
            <textarea rows="2" bind:value={brokerCaCert} disabled={savingBroker}></textarea>
          </label>
          <label class="field">
            <span>Client certificate {broker?.hasClientCert ? '(set — leave blank to keep)' : '(optional, PEM)'}</span>
            <textarea rows="2" bind:value={brokerClientCert} disabled={savingBroker}></textarea>
          </label>
          <label class="field">
            <span>Client private key {broker?.hasClientKey ? '(set — leave blank to keep)' : '(optional, PEM)'}</span>
            <textarea rows="2" bind:value={brokerClientKey} disabled={savingBroker}></textarea>
          </label>
          <div class="form-actions">
            <Button type="submit" disabled={savingBroker}>
              {savingBroker ? 'Saving…' : broker ? 'Save changes' : 'Create'}
            </Button>
            {#if broker}
              <Button type="button" variant="danger" disabled={savingBroker || deletingBroker} onclick={() => void onDeleteBroker()}>
                {deletingBroker ? 'Deleting…' : 'Delete broker'}
              </Button>
            {/if}
          </div>
        </form>
        {#if brokerFormError}
          <p class="err" role="alert">{brokerFormError}</p>
        {/if}
        {#if brokerFormOk}
          <p class="ok" role="status">{brokerFormOk}</p>
        {/if}
      </Card>

      <div class="grid">
        <Card title="MQTT channels" subtitle={`${channels.length} of ${CAP} used`} padded={false}>
          {#if channels.length === 0}
            <p class="banner muted">No channels yet.</p>
          {:else}
            <ul class="list">
              {#each channels as c (c.id)}
                <li class:selected={editingChannelId === c.id}>
                  <button class="row" onclick={() => editChannel(c)}>
                    <span class="name">{c.name}</span>
                    <span class="meta">pub {c.pubTopic}</span>
                  </button>
                  <Button variant="ghost" size="sm" disabled={busyDeleteChannelId === c.id} onclick={() => void onDeleteChannel(c)}>
                    {busyDeleteChannelId === c.id ? '…' : 'Delete'}
                  </Button>
                </li>
              {/each}
            </ul>
          {/if}
          {#if channelDeleteError}
            <p class="err banner-pad" role="alert">{channelDeleteError}</p>
          {/if}
        </Card>

        <Card title={editingChannelId ? 'Edit channel' : 'New channel'} subtitle={!broker ? 'Set up your broker first' : atChannelCap && !editingChannelId ? `Limit of ${CAP} reached` : undefined}>
          {#if !broker}
            <p class="muted">Set up your MQTT broker above before adding a channel.</p>
          {:else if atChannelCap && !editingChannelId}
            <p class="muted">Delete an existing channel before adding another.</p>
          {:else}
            <form class="form" onsubmit={(e) => void onSaveChannel(e)}>
              <label class="field">
                <span>Name</span>
                <input type="text" bind:value={channelName} maxlength="120" disabled={savingChannel} required />
              </label>
              <label class="field">
                <span>Publish topic</span>
                <input
                  type="text"
                  bind:value={channelPubTopic}
                  placeholder="ucc/{'{groupName}'}/{'{deviceName}'}/{'{uid}'}/data"
                  disabled={savingChannel}
                  required
                />
                <span class="field-hint">Device data going upstream. {PLACEHOLDER_HINT}</span>
              </label>
              <label class="field">
                <span>Subscribe topic (optional)</span>
                <input type="text" bind:value={channelSubTopic} placeholder="ucc/{'{uid}'}/cmd" disabled={savingChannel} />
                <span class="field-hint">
                  Command from platform to a group of devices or a single device. {PLACEHOLDER_HINT}
                </span>
              </label>
              <div class="form-actions">
                <Button type="submit" disabled={savingChannel}>
                  {savingChannel ? 'Saving…' : editingChannelId ? 'Save changes' : 'Create'}
                </Button>
                {#if editingChannelId}
                  <Button type="button" variant="ghost" disabled={savingChannel} onclick={resetChannelForm}>Cancel</Button>
                {/if}
              </div>
            </form>
          {/if}
          {#if channelFormError}
            <p class="err" role="alert">{channelFormError}</p>
          {/if}
          {#if channelFormOk}
            <p class="ok" role="status">{channelFormOk}</p>
          {/if}
        </Card>
      </div>
    </div>
  {/if}
</div>

<style>
  .page {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    max-width: 72rem;
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }
  .sub {
    margin: 0 0 0.5rem;
    color: var(--text-muted);
    font-size: 0.95rem;
    line-height: 1.45;
    max-width: 52rem;
  }
  .tabs {
    display: flex;
    gap: 0.5rem;
  }
  .tab {
    font: inherit;
    padding: 0.4rem 0.9rem;
    border-radius: 999px;
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--text-muted);
    cursor: pointer;
  }
  .tab.active {
    background: var(--accent, #2563eb);
    color: white;
    border-color: transparent;
  }
  .stack {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .grid {
    display: grid;
    grid-template-columns: 1fr 1.4fr;
    gap: 1rem;
    align-items: start;
  }
  @media (max-width: 900px) {
    .grid {
      grid-template-columns: 1fr;
    }
  }
  .muted {
    color: var(--text-muted);
  }
  .err {
    color: #f87171;
    margin: 0.5rem 0 0;
    font-size: 0.9rem;
  }
  .ok {
    color: var(--success, #22c55e);
    margin: 0.5rem 0 0;
    font-size: 0.9rem;
  }
  .banner.muted {
    padding: 1rem;
    margin: 0;
  }
  .banner-pad {
    padding: 0 1rem 0.75rem;
  }
  .list {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .list li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid var(--border);
  }
  .list li.selected {
    background: var(--surface-2, #f9fafb);
  }
  .row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.15rem;
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    text-align: left;
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }
  .name {
    font-weight: 600;
  }
  .meta {
    font-size: 0.8rem;
    color: var(--text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
  }
  .row2 {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 0.65rem;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    font-size: 0.9rem;
  }
  .field span {
    color: var(--text-muted);
    font-size: 0.8rem;
  }
  .field-hint {
    font-size: 0.75rem;
    color: var(--text-muted);
    opacity: 0.85;
  }
  .field input,
  .field textarea,
  .field select {
    font: inherit;
    padding: 0.45rem 0.6rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border);
    background: var(--surface-2, #fff);
    color: inherit;
    resize: vertical;
  }
  .field select {
    appearance: none;
    background-image: linear-gradient(45deg, transparent 50%, currentColor 50%),
      linear-gradient(135deg, currentColor 50%, transparent 50%);
    background-position:
      calc(100% - 1.15rem) center,
      calc(100% - 0.8rem) center;
    background-size: 0.35rem 0.35rem;
    background-repeat: no-repeat;
    padding-right: 1.75rem;
  }
  .header-rows {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin-bottom: 0.4rem;
  }
  .header-row {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 0.4rem;
    align-items: center;
  }
  .header-row input {
    font: inherit;
    padding: 0.4rem 0.55rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border);
    background: var(--surface-2, #fff);
    color: inherit;
  }
  .remove-row {
    font: inherit;
    line-height: 1;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border);
    background: var(--surface-2, #fff);
    color: var(--text-muted);
    cursor: pointer;
  }
  .remove-row:hover {
    color: var(--danger, #dc2626);
  }
  .form-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }
</style>
